import React, { Component } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSkull,
  faRedo,
  faBug,
  faHome,
  faAnchor,
} from '@fortawesome/free-solid-svg-icons';
import './ErrorBoundary.css';

/**
 * Error classification for different handling strategies
 */
const classifyError = (error) => {
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorName = error?.name?.toLowerCase() || '';

  // Network errors
  if (
    errorMessage.includes('network') ||
    errorMessage.includes('fetch') ||
    errorMessage.includes('connection') ||
    errorName.includes('networkerror')
  ) {
    return 'network';
  }

  // Authentication errors
  if (
    errorMessage.includes('auth') ||
    errorMessage.includes('permission') ||
    errorMessage.includes('unauthorized') ||
    errorMessage.includes('forbidden')
  ) {
    return 'auth';
  }

  // Chunk loading errors (code splitting issues)
  if (
    errorMessage.includes('loading chunk') ||
    errorMessage.includes('loading css chunk')
  ) {
    return 'chunk';
  }

  return 'unknown';
};

/**
 * Get user-friendly error message based on classification
 */
const getErrorMessage = (type) => {
  switch (type) {
    case 'network':
      return {
        title: "Blimey! We've lost our bearings!",
        message: "Looks like the connection to our servers has been severed. Check your internet connection and try again.",
        canRetry: true,
      };
    case 'auth':
      return {
        title: "Avast! Ye need to identify yerself!",
        message: "Your session may have expired or you don't have permission to view this content.",
        canRetry: false,
        showLogin: true,
      };
    case 'chunk':
      return {
        title: "A new version be available!",
        message: "We've updated our treasure maps. Please refresh the page to get the latest version.",
        canRetry: true,
        forceRefresh: true,
      };
    default:
      return {
        title: "Shiver me timbers! Something went wrong!",
        message: "An unexpected error occurred. Our crew has been notified and is working to fix it.",
        canRetry: true,
      };
  }
};

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree and displays fallback UI
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorType: 'unknown',
      retryCount: 0,
      showDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error,
      errorType: classifyError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log error to console for development
    console.error('[ErrorBoundary] Caught error:', error);
    console.error('[ErrorBoundary] Error info:', errorInfo);

    // TODO: Send error to logging service
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // This would send to your error tracking service (e.g., Sentry, LogRocket)
    // For now, we'll store it for potential reporting
    const errorReport = {
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    };

    // Store in sessionStorage for "Report Issue" feature
    try {
      sessionStorage.setItem('lastError', JSON.stringify(errorReport));
    } catch (e) {
      console.error('Failed to store error report:', e);
    }
  };

  handleRetry = () => {
    const { errorType, retryCount } = this.state;
    const errorConfig = getErrorMessage(errorType);

    // Force page refresh for chunk errors
    if (errorConfig.forceRefresh) {
      window.location.reload();
      return;
    }

    // Increment retry count and reset error state
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: retryCount + 1,
    });

    // Call onRetry callback if provided
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReportIssue = () => {
    const errorReport = sessionStorage.getItem('lastError');
    
    // Create mailto link with error details
    const subject = encodeURIComponent('Error Report - Pirates Guild App');
    const body = encodeURIComponent(
      `I encountered an error while using the app.\n\n` +
      `Page: ${window.location.href}\n` +
      `Time: ${new Date().toLocaleString()}\n\n` +
      `Please describe what you were doing when the error occurred:\n\n` +
      `---\n` +
      `Technical Details (do not edit):\n` +
      `${errorReport || 'No details available'}`
    );

    window.location.href = `mailto:support@piratesguild.com?subject=${subject}&body=${body}`;
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  render() {
    const { hasError, error, errorInfo, errorType, retryCount, showDetails } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    // Allow custom fallback UI
    if (fallback) {
      return fallback({ error, errorInfo, retry: this.handleRetry });
    }

    const errorConfig = getErrorMessage(errorType);
    const maxRetries = 3;
    const canRetry = errorConfig.canRetry && retryCount < maxRetries;

    return (
      <div className="error-boundary">
        <div className="error-boundary-content">
          {/* Pirate skull icon */}
          <div className="error-icon">
            <FontAwesomeIcon icon={faSkull} />
          </div>

          {/* Error title */}
          <h1 className="error-title">{errorConfig.title}</h1>

          {/* Error message */}
          <p className="error-message">{errorConfig.message}</p>

          {/* Retry count warning */}
          {retryCount > 0 && canRetry && (
            <p className="error-retry-count">
              Attempt {retryCount} of {maxRetries}
            </p>
          )}

          {/* Action buttons */}
          <div className="error-actions">
            {canRetry && (
              <button className="error-btn primary" onClick={this.handleRetry}>
                <FontAwesomeIcon icon={faRedo} />
                <span>Try Again</span>
              </button>
            )}

            <button className="error-btn secondary" onClick={this.handleGoHome}>
              <FontAwesomeIcon icon={faHome} />
              <span>Go Home</span>
            </button>

            <button className="error-btn tertiary" onClick={this.handleReportIssue}>
              <FontAwesomeIcon icon={faBug} />
              <span>Report Issue</span>
            </button>
          </div>

          {/* Technical details (collapsible) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="error-details-section">
              <button className="error-details-toggle" onClick={this.toggleDetails}>
                <FontAwesomeIcon icon={faAnchor} />
                <span>{showDetails ? 'Hide' : 'Show'} Technical Details</span>
              </button>

              {showDetails && (
                <div className="error-details">
                  <div className="error-detail-block">
                    <strong>Error:</strong>
                    <pre>{error?.toString()}</pre>
                  </div>
                  {error?.stack && (
                    <div className="error-detail-block">
                      <strong>Stack Trace:</strong>
                      <pre>{error.stack}</pre>
                    </div>
                  )}
                  {errorInfo?.componentStack && (
                    <div className="error-detail-block">
                      <strong>Component Stack:</strong>
                      <pre>{errorInfo.componentStack}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Decorative wave */}
        <div className="error-wave">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path
              fill="rgba(239, 68, 68, 0.1)"
              d="M0,192L60,176C120,160,240,128,360,133.3C480,139,600,181,720,192C840,203,960,181,1080,160C1200,139,1320,117,1380,106.7L1440,96L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
            />
          </svg>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
